import { CONFIG } from "~/config";
import { v4 as uuidv4 } from "uuid";
import { store } from "~/state/store";
import { IUserSubscriptions } from "~common/types/user";
import { ELoginState } from "~/state/user/interfaces";

interface IFandaUser {
	id: number;
	activeDays?: number;
	isFanda?: boolean;
	premium?: boolean;
	progressActual?: number;
	progressBoundary: number;
	totalKilometers?: number;
	totalPhotos?: number;
	totalReviews?: number;
	totalTrafficFeedbacks?: number;
	totalUserPoiCreated?: number;
	totalUserPoiUpdated?: number;
}

export interface IFandaMe {
	user: Required<IFandaUser>;
}

export interface IProductPrices {
	[key: string]: {
		currency: string;
		amount: number;
	};
}

export interface IProductInfo {
	data: [{
		features: {
			[key: string]: boolean;
		};
		platformProduct: Array<string>;
		typeId: string;
		prices?: IProductPrices;
	}];
}

export interface IUserPremium {
	id: number;
	premium: boolean;
	isFanda?: boolean;
}

function getUserPremiumData() {
	const user = store.getState().user.user;

	return user.premiumData;
}

export async function getUserFandaProgress(): Promise<IFandaMe> {
	try {
		const response = await fetch(`https://pro.${CONFIG.BASE_DOMAIN}/user-info/users/me`, {
			credentials: "include",
		});
		const data = await response.json() as { user: IFandaUser };

		return Promise.resolve({
			user: {
				activeDays: 0,
				isFanda: false,
				premium: false,
				progressActual: 0,
				totalKilometers: 0,
				totalPhotos: 0,
				totalReviews: 0,
				totalTrafficFeedbacks: 0,
				totalUserPoiCreated: 0,
				totalUserPoiUpdated: 0,
				...data.user,
			},
		});
	} catch (ex) {
		return Promise.reject(ex);
	}
}

export async function getUsersPremiumByRusIds(userIds: Array<number>): Promise<Array<IUserPremium>> {
	try {
		const response = await fetch(`https://pro.${CONFIG.BASE_DOMAIN}/user-info/users?userIds=${userIds.join(",")}`, {
			credentials: "include",
		});
		const data = await response.json() as { users: Array<IUserPremium> };

		return data.users;
	} catch (ex) {
		return Promise.reject(ex);
	}
}

export async function getUserPremiumByUserPublicId(userPublicId: string): Promise<IUserPremium> {
	try {
		const response = await fetch(`https://pro.${CONFIG.BASE_DOMAIN}/user-info/users/${userPublicId}`, {
			credentials: "include",
		});
		const data = await response.json() as { user: IUserPremium };

		return data.user;
	} catch (ex) {
		return Promise.reject(ex);
	}
}

async function productInfo(typeId, isTrialAvailable = false): Promise<IProductInfo> {
	const { user } = store.getState().user;

	const response = await fetch(`${CONFIG.requestUrl}/subscriptions/type/${typeId}?isTrial=${isTrialAvailable ? "true" : "false"}&platform=paddle`, {
		method: "GET",
		headers: {
			"Accept": "application/json",
			"Content-Type": "application/json",
			"X-Correlation-Id": uuidv4(),
		},
		credentials: user.state === ELoginState.Green ? "include" : "omit",
	});

	if (response?.status === 200) {
		const responseData = await response.json();

		return responseData;
	}

	return null;
}

export async function getProductInfo() {
	const premiumData = getUserPremiumData();
	const productInfoData = await productInfo(premiumData.availableProducts.join(",") || CONFIG.availableProducts.join(","), premiumData?.profile?.isTrialAvailable);

	return productInfoData;
}

export async function payComplete(transactionId: string): Promise<IUserSubscriptions> {
	const premiumData = getUserPremiumData();
	const response = await fetch(`${CONFIG.requestUrl}/subscriptions/subscription/me`, {
		method: "POST",
		headers: {
			"Accept": "application/json",
			"Content-Type": "application/json",
			"X-Correlation-Id": uuidv4(),
		},
		credentials: "include",
		body: JSON.stringify({
			language: premiumData?.profile?.language || CONFIG.defaultLang,
			country: premiumData?.profile?.country || "",
			paymentProvider: "paddle",
			token: transactionId,
		}),
	});

	if (response?.status === 200) {
		const responseData = await response.json();

		return responseData;
	}

	return null;
}
